import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { getHeroImage } from '../utils/heroImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArticleCard = ({ article }) => (
  <Link
    to={`/article/${article.slug || article.id}`}
    className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden"
    data-testid={`article-${article.slug || article.id}`}
  >
    <img src={getHeroImage(article.category)} alt={article.title} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
    <div className="p-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">{article.category.replace(/-/g, ' ')}</span>
        <span className="text-gray-400">&bull;</span>
        <span className="text-xs text-gray-500">{article.read_time} min read</span>
      </div>
      <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">{article.title}</h3>
      <p className="text-gray-600 mb-4 line-clamp-3">{article.excerpt}</p>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">{article.author}</span>
        <ArrowRight className="text-blue-600 group-hover:translate-x-1 transition-transform" size={20} />
      </div>
      {article.featured && (
        <div className="mt-3">
          <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">Featured</span>
        </div>
      )}
    </div>
  </Link>
);

const CategoryPage = () => {
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCategoryData = useCallback(async () => {
    setLoading(true);
    try {
      const [categoriesRes, articlesRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/articles/category/${categorySlug}`)
      ]);
      const foundCategory = categoriesRes.data.find(c => c.slug === categorySlug);
      setCategory(foundCategory);
      setArticles(articlesRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error loading category data:', error);
    } finally {
      setLoading(false);
    }
  }, [categorySlug]);

  useEffect(() => {
    loadCategoryData();
  }, [loadCategoryData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center"><div className="text-xl text-gray-600">Loading...</div></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <div className="text-xl text-gray-600">Category not found</div>
          <Link to="/research" className="text-blue-600 hover:underline mt-4 inline-block">Return to Research Library</Link>
        </div>
      </div>
    );
  }

  const categoryUrl = `https://www.peptidesciences.ai/research/${category.slug}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{`${category.name} Peptide Research | PeptideSciences.ai`}</title>
        <meta name="description" content={`Evidence-based research articles on ${category.name.toLowerCase()} peptides. ${category.description}`} />
        <link rel="canonical" href={categoryUrl} />
        <meta property="og:title" content={`${category.name} Peptide Research | PeptideSciences.ai`} />
        <meta property="og:description" content={category.description} />
        <meta property="og:url" content={categoryUrl} />
      </Helmet>
      <Navigation />

      <section className="bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900 text-white py-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_07859234-c0aa-4e8d-94c2-c8d61ccd3354/artifacts/a25q64nh_Peptide%20Background.png)', backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-slate-800/85 to-gray-900/90"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <Link to="/research" className="inline-flex items-center text-gray-300 hover:text-white mb-6" data-testid="back-to-research">
            <ArrowLeft className="mr-2" size={20} /> Back to Research
          </Link>
          <h1 className="text-5xl font-bold mb-4" data-testid="category-title">{category.name}</h1>
          <p className="text-lg text-gray-300">{category.description}</p>
          <div className="mt-6"><span className="text-gray-400">{articles.length} article{articles.length !== 1 ? 's' : ''} in this category</span></div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {articles.length === 0 ? (
          <div className="text-center py-20"><div className="text-xl text-gray-600">No articles found in this category yet.</div></div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="category-articles-grid">
            {articles.map((article) => <ArticleCard key={article.id} article={article} />)}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CategoryPage;
